import {
  createApp,
  defineComponent,
  h,
  Comment,
  Text,
  type Slots,
  type VNode,
  type App,
  getCurrentInstance,
} from 'vue';
import type { ZodSafeParseResult } from 'zod';
import camelCase from 'lodash/camelCase';
import kebabCase from 'lodash/kebabCase';
import groupBy from 'lodash/groupBy';
import mergeWith from 'lodash/mergeWith';
import { vnodesGetter } from './components';

type GetVNodeProps = Record<string, string | string[] | GetVNodeProps[]>;
export type GetVNode = (props: GetVNodeProps, slots?: Slots) => VNode;
export type GetVNodeFunction = (name: string) => GetVNode | undefined;

// Merge props and make arrays from props that are repeated
const mergeProps = (
  base: GetVNodeProps,
  ...otherProps: GetVNodeProps[]
): GetVNodeProps =>
  otherProps.reduce<GetVNodeProps>(
    (result, props) =>
      mergeWith(result, props, (targetValue, sourceValue) => {
        if (Array.isArray(targetValue)) {
          return [...targetValue, sourceValue];
        }
        if (targetValue !== undefined) {
          return [targetValue, sourceValue];
        }
        return sourceValue;
      }),
    base
  );

const getPropsFromElement = (el: HTMLElement) => {
  const getPropsFromQueryString = (
    queryString: string,
    element: HTMLElement
  ): GetVNodeProps => {
    const queryItems = Array.from(new URLSearchParams(queryString).entries());
    return queryItems.reduce<GetVNodeProps>((result, [key, value]) => {
      const kebabValue = kebabCase(value);
      if (kebabValue === 'text-content') {
        return mergeProps(result, { [key]: element.textContent || '' });
      }
      return mergeProps(result, { [key]: element.getAttribute(value) });
    }, {});
  };

  const getPropsFromAttributeList = (
    attributes: Attr[],
    element: HTMLElement
  ): GetVNodeProps =>
    attributes.reduce<GetVNodeProps>((result, attr) => {
      if (['v-cloak', 'data-component'].includes(kebabCase(attr.name))) {
        return result;
      }
      if (attr.name.toLowerCase() === 'data-props') {
        return mergeProps(result, getPropsFromQueryString(attr.value, element));
      }
      return mergeProps(result, { [attr.name]: attr.value });
    }, {});

  const attrProps = getPropsFromAttributeList(Array.from(el.attributes), el);

  const elementsWithDataProps = Array.from(el.children).filter(
    (child: HTMLElement) => child.dataset.props && !child.dataset.component
  );

  const allProps = elementsWithDataProps.reduce<GetVNodeProps>(
    (result, child: HTMLElement) =>
      mergeProps(result, getPropsFromQueryString(child.dataset.props, child)),
    attrProps
  );

  const jsonParsedProps: GetVNodeProps = Object.fromEntries(
    Object.entries(allProps).map(([key, value]) => {
      if (key.toLowerCase().endsWith('-json') && typeof value === 'string') {
        const json = value
          .replace(/^True$/, 'true')
          .replace(/^False$/, 'false');

        const parsedValue = json === '' ? undefined : JSON.parse(json);
        return [key, parsedValue];
      }
      if (key === value) {
        return [key, true];
      }
      if (value === null) {
        return [key, undefined];
      }
      return [key, value];
    })
  );

  // normalize prop names
  const normalizeKey = (key: string) => {
    const match = /^(data-)?(.*?)(-json)?$/i.exec(kebabCase(key));
    return camelCase(match[2]);
  };
  return Object.entries(jsonParsedProps).reduce<GetVNodeProps>(
    (result, [key, value]) => ({
      ...result,
      [key]: value,
      [normalizeKey(key)]: value,
    }),
    {}
  );
};

const getVNodeFromElement = (element: HTMLElement): VNode => {
  const componentName = element.dataset.component;
  const getVNodesFunction = vnodesGetter(componentName);

  if (componentName && !getVNodesFunction) {
    // eslint-disable-next-line no-console
    console.error(`Component ${componentName} not found.`);
  }

  const nodesToVNodes = (childNodes: Node[]): VNode[] =>
    childNodes.reduce<VNode[]>((result, node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent) {
        // text
        return [...result, h(Text, node.textContent)];
      }
      if (node.nodeType === Node.TEXT_NODE && node.textContent) {
        // comment
        return [...result, h(Comment, node.textContent)];
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        return [...result, getVNodeFromElement(el)];
      }
      return result;
    }, []);

  const getVNodeSlots = (nodes: Node[]): Slots => {
    const groupedNodes: [string, Node[]][] = Object.entries(
      groupBy(nodes, (node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as HTMLElement;
          return el.getAttribute('slot') || el.dataset.slot || 'default';
        }
        return 'default';
      })
    );
    const groupedVNodes: [string, VNode[]][] = groupedNodes.map(
      ([slotName, groupNodes]) => [slotName, nodesToVNodes(groupNodes)]
    );
    return Object.fromEntries(
      groupedVNodes.map(([slotName, vNodes]) => [slotName, () => vNodes])
    );
  };

  if (getVNodesFunction) {
    if (import.meta.env.MODE === 'development') {
      // eslint-disable-next-line no-console
      console.info(
        `[semantic-html-to-vue] Rendering component: ${componentName}`
      );
    }
    const props = getPropsFromElement(element);
    const childElements = Array.from(element.children).filter(
      (child: HTMLElement) => !child.dataset.props || child.dataset.component
    );
    const slots = getVNodeSlots(childElements);
    return getVNodesFunction(props, slots);
  }

  const childVNodes = nodesToVNodes(Array.from(element.childNodes));
  const props = Object.fromEntries(
    Array.from(element.attributes).map(({ name, value }) => [name, value])
  );
  return h(element.tagName, props, childVNodes);
};

export const initiateVueIslands = (
  doc: Document,
  configureApp?: (app: App) => Promise<void>
) => {
  Array.from(doc.querySelectorAll<HTMLElement>('[data-component]'))
    .filter((element) => !element.parentElement.closest('[data-component]'))
    .reduce<{ vNode: VNode; element: HTMLElement }[]>((result, element) => {
      const componentName = kebabCase(element.dataset.component);
      if (!vnodesGetter(componentName)) {
        // eslint-disable-next-line no-console
        console.error(`Component ${componentName} not found.`);
        return result;
      }
      return [...result, { element, vNode: getVNodeFromElement(element) }];
    }, [])
    .forEach(async ({ element, vNode }) => {
      const nodeName = element.nodeName.toLowerCase() ?? 'div';
      const mountElement = document.createElement(nodeName);
      mountElement.dataset.component = element.dataset.component;
      element.parentElement.replaceChild(mountElement, element);

      const app = createApp(defineComponent(() => () => vNode));
      if (configureApp) {
        await configureApp(app);
      }
      app.mount(mountElement);
    });
};

export type WithRawProps<
  Props extends Record<string, unknown> = Record<string, unknown>,
> = {
  rawProps?: Partial<Props>;
};
export const validateRawProps = <Props extends object & WithRawProps>(
  result: ZodSafeParseResult<Props>,
  props: Props
) => {
  if (!result.success) {
    const instance = getCurrentInstance();
    // eslint-disable-next-line no-console
    console.error(
      // eslint-disable-next-line no-underscore-dangle
      `Props validation error in component "${instance.type.__name}": ${result.error.message}\n\nRaw props:`,
      props.rawProps
    );
  }
};
